import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePaystackPayment } from 'react-paystack';
import { useBooking } from '../../context/BookingContext';
import { bookingService } from '../../services/booking';
import { generateInvoice } from '../../utils/pdfGenerator';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

type BookingStatus = 'idle' | 'processing' | 'confirming' | 'success' | 'error';

export default function Payment() {
    const location = useLocation();
    const navigate = useNavigate();
    const { clearCart, formatPrice } = useBooking();

    const { passengers, contactEmail, contactPhone, totalAmount, cartItems } = location.state || {};

    const [status, setStatus] = useState<BookingStatus>('idle');
    const [errorMessage, setErrorMessage] = useState('');
    const [bookingResult, setBookingResult] = useState<any>(null);
    const [paystackKey, setPaystackKey] = useState<string>('');
    const [isKeyLoading, setIsKeyLoading] = useState(true);

    const queryParams = new URLSearchParams(location.search);
    const callbackRef = queryParams.get('reference') || queryParams.get('trxref');

    useEffect(() => {
        // If arriving from Paystack Callback URL with reference param
        if (callbackRef) {
            setStatus('confirming');
            bookingService.verifyBookingReference(callbackRef)
                .then((res: any) => {
                    if (res?.success && res?.booking) {
                        setBookingResult(res.booking);
                        setStatus('success');
                    } else if (res?.success && res?.paystack_status === 'success') {
                        setBookingResult({
                            payment_reference: callbackRef,
                            amount: res.amount,
                            status: 'paid',
                            message: 'Payment verified successfully.'
                        });
                        setStatus('success');
                    } else {
                        setErrorMessage('Payment received. Processing your reservation fulfillment.');
                        setStatus('error');
                    }
                })
                .catch(() => {
                    setErrorMessage('Payment completed. Please check your dashboard to view your booking.');
                    setStatus('error');
                });
            return;
        }

        if (!passengers || !contactEmail || !cartItems) {
            navigate('/booking/checkout');
            return;
        }

        // Dynamically fetch Paystack public key from server (always pulls from Admin DB)
        setIsKeyLoading(true);
        bookingService.getPaymentConfig()
            .then((res: any) => {
                if (res?.paystackPublicKey) {
                    setPaystackKey(res.paystackPublicKey.trim());
                } else if (import.meta.env.VITE_PAYSTACK_PUBLIC_KEY) {
                    setPaystackKey(import.meta.env.VITE_PAYSTACK_PUBLIC_KEY.trim());
                }
            })
            .catch((e) => {
                console.warn('Could not load payment config:', e);
                if (import.meta.env.VITE_PAYSTACK_PUBLIC_KEY) {
                    setPaystackKey(import.meta.env.VITE_PAYSTACK_PUBLIC_KEY.trim());
                }
            })
            .finally(() => {
                setIsKeyLoading(false);
            });
    }, [passengers, contactEmail, cartItems, navigate, callbackRef]);

    // Paystack Config
    const config = {
        reference: `RIG_${Date.now()}_${Math.random().toString(36).substr(2, 6).toUpperCase()}`,
        email: contactEmail,
        amount: Math.round(Number(totalAmount || 0) * 100), // Paystack expects kobo (NGN * 100)
        publicKey: paystackKey,
        currency: 'NGN',
        metadata: {
            custom_fields: [
                { display_name: "Service Type", variable_name: "service_type", value: cartItems?.[0]?.type || 'hotel' },
                { display_name: "Passengers", variable_name: "passenger_count", value: passengers?.length || 1 },
                { display_name: "Customer Phone", variable_name: "customer_phone", value: contactPhone }
            ]
        }
    };

    const onSuccess = async (reference: any) => {
        setStatus('confirming');

        try {
            // For each cart item, create a booking and process with LetSFG
            let lastResult: any = null;
            for (const item of cartItems) {
                const effectiveSessionId = item.session_id || item.search_id || item.details?.session_id || item.details?.search_id || null;
                const effectiveOfferId = item.offer_id || item.details?.offer_id || (item.details as any)?.id || null;

                const result = await bookingService.createBooking({
                    payment_reference: reference.reference,
                    service_type: item.type,
                    amount: item.price,
                    search_id: effectiveSessionId,
                    offer_id: effectiveOfferId,
                    passengers: passengers,
                    contact_email: contactEmail,
                    contact_phone: contactPhone,
                    booking_details: {
                        ...item.details,
                        search_id: effectiveSessionId,
                        session_id: effectiveSessionId,
                        offer_id: effectiveOfferId,
                        hotel_code: item.details?.hotel_code || effectiveOfferId,
                        combination_id_v2: item.details?.combination_id_v2 || effectiveOfferId,
                        expected_price: item.details?.expected_price !== undefined ? item.details.expected_price : (item.rawPriceUSD || Math.round(Number(item.price) / 1600)),
                        expected_cost: item.details?.expected_cost !== undefined ? item.details.expected_cost : (item.details?.expected_price !== undefined ? item.details.expected_price : (item.rawPriceUSD || Math.round(Number(item.price) / 1600))),
                        currency: item.details?.offer_currency || item.details?.currency || 'USD',
                        fx_rate: item.details?.fx_rate !== undefined ? item.details.fx_rate : 1,
                        raw_offer: item.details?.raw_offer || (item as any)?.raw_offer,
                        city_id: item.details?.city_id || 9821,
                        city_name: item.details?.city_name || 'Lagos',
                        check_in: item.details?.check_in || item.details?.checkIn || new Date().toISOString().split('T')[0],
                        check_out: item.details?.check_out || item.details?.checkOut || new Date(Date.now() + 86400000).toISOString().split('T')[0],
                        adults: item.details?.guests || passengers?.length || 1,
                        guests: item.details?.guests || passengers?.length || 1,
                        email: contactEmail,
                        phone: contactPhone
                    }
                });

                lastResult = result;
            }

            setBookingResult(lastResult);
            setStatus('success');
            clearCart();
        } catch (error: any) {
            console.error('Booking creation error:', error);
            setErrorMessage(error.message || 'Payment received, but reservation fulfillment encountered a delay. Our team will contact you shortly.');
            setStatus('error');
        }
    };

    const onClose = async () => {
        setStatus('idle');
        try {
            // Log failed / cancelled attempt for User Dashboard
            await bookingService.recordFailedAttempt({
                payment_reference: config.reference,
                service_type: cartItems?.[0]?.type || 'hotel',
                amount: Number(totalAmount || 0),
                reason: 'Customer cancelled or closed Paystack payment window',
                contact_email: contactEmail,
                contact_phone: contactPhone,
                booking_details: {
                    cartItems,
                    passengers
                }
            });
        } catch (e) {
            console.warn('Could not record failed attempt:', e);
        }
    };

    const initializePayment = usePaystackPayment(config);

    if (isKeyLoading && !callbackRef) {
        return (
            <div className="min-h-screen bg-gray-950 flex flex-col items-center justify-center text-white">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-logo-gold-400 mb-4"></div>
                <p className="text-gray-400 font-medium text-sm">Connecting to secure live payment gateway...</p>
            </div>
        );
    }

    if (!passengers && !callbackRef) return null;

    return (
        <>
            <Header />
            <div className="min-h-screen pt-24 pb-12 px-4 flex items-center justify-center bg-gray-950">

                {/* ─── Success State ─── */}
                {status === 'success' && (
                    <div className="rounded-3xl p-8 md:p-10 max-w-lg w-full text-center bg-slate-900/90 border border-white/20 shadow-2xl backdrop-blur-xl animate-fade-in">
                        <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 bg-green-500/15 border border-green-500/30">
                            <i className="ri-check-double-line text-4xl text-green-400"></i>
                        </div>
                        <h1 className="text-3xl font-extrabold text-white mb-2">Booking Confirmed!</h1>
                        <p className="text-slate-300 text-sm mb-4">
                            {bookingResult?.message || 'Your reservation has been confirmed with LetSFG and the provider.'}
                        </p>

                        {/* PNR Banner */}
                        {bookingResult?.pnr ? (
                            <div className="my-5 px-6 py-4 rounded-2xl bg-[#b8a080]/15 border border-[#b8a080]/40 shadow-inner">
                                <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">Airline Reservation Code (PNR)</p>
                                <p className="text-3xl font-mono font-extrabold text-[#b8a080] tracking-widest">{bookingResult.pnr}</p>
                            </div>
                        ) : bookingResult?.letsfg_booking_id ? (
                            <div className="my-5 px-6 py-4 rounded-2xl bg-[#b8a080]/15 border border-[#b8a080]/40 shadow-inner">
                                <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">LetSFG Booking Reference</p>
                                <p className="text-xl font-mono font-bold text-[#b8a080]">{bookingResult.letsfg_booking_id}</p>
                            </div>
                        ) : null}

                        {/* Details summary */}
                        <div className="bg-white/5 rounded-2xl p-4 text-left space-y-2 mb-6 border border-white/10 text-xs text-gray-300">
                            <div className="flex justify-between">
                                <span className="text-gray-400">Payment Reference:</span>
                                <span className="font-mono text-white font-medium">{bookingResult?.payment_reference || config.reference}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">Total Paid:</span>
                                <span className="font-bold text-green-400">{formatPrice(totalAmount)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">Lead Passenger:</span>
                                <span className="text-white font-medium">{passengers[0]?.firstName || passengers[0]?.given_name} {passengers[0]?.lastName || passengers[0]?.family_name}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">Confirmation Sent To:</span>
                                <span className="text-white font-medium">{contactEmail}</span>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <button
                                onClick={() => navigate('/booking/my-bookings?tab=tickets')}
                                className="px-6 py-3 rounded-xl font-bold text-white transition-all shadow-lg shadow-[#b8a080]/30 hover:shadow-[#b8a080]/40 cursor-pointer"
                                style={{ background: 'linear-gradient(135deg, #b8a080 0%, #8a7060 100%)' }}
                            >
                                <i className="ri-ticket-line mr-2"></i>
                                View My Tickets
                            </button>
                            <button
                                onClick={() => {
                                    if (bookingResult) {
                                        generateInvoice({
                                            id: bookingResult.booking_id,
                                            created_at: new Date().toISOString(),
                                            amount: totalAmount,
                                            currency: 'NGN',
                                            service_type: cartItems?.[0]?.type || 'flight',
                                            status: 'confirmed',
                                            booking_details: {
                                                contact_email: contactEmail,
                                                contact_phone: contactPhone,
                                                passengers: passengers,
                                                pnr: bookingResult.pnr,
                                                payment_reference: bookingResult.payment_reference || config.reference
                                            }
                                        });
                                    }
                                }}
                                className="px-5 py-3 rounded-xl font-semibold text-slate-200 hover:text-white bg-white/10 hover:bg-white/15 border border-white/20 transition-colors cursor-pointer"
                            >
                                <i className="ri-file-download-line mr-2"></i>
                                Download Invoice
                            </button>
                        </div>
                    </div>
                )}

                {/* ─── Error State ─── */}
                {status === 'error' && (
                    <div className="rounded-3xl p-8 md:p-10 max-w-lg w-full text-center bg-slate-900/90 border border-red-500/30 shadow-2xl backdrop-blur-xl">
                        <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 bg-red-500/15 border border-red-500/30">
                            <i className="ri-error-warning-line text-4xl text-red-400"></i>
                        </div>
                        <h1 className="text-2xl font-bold text-white mb-2">Booking Status Notice</h1>
                        <p className="text-slate-300 text-sm mb-6 leading-relaxed">{errorMessage}</p>
                        
                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={() => setStatus('idle')}
                                className="px-6 py-3 rounded-xl font-bold text-white bg-white/10 hover:bg-white/20 border border-white/20 transition-all cursor-pointer"
                            >
                                Try Again
                            </button>
                            <button
                                onClick={() => navigate('/booking/my-bookings')}
                                className="px-6 py-3 rounded-xl font-bold text-white cursor-pointer"
                                style={{ background: 'linear-gradient(135deg, #b8a080 0%, #8a7060 100%)' }}
                            >
                                Check My Bookings
                            </button>
                        </div>
                    </div>
                )}

                {/* ─── Processing / Confirming State ─── */}
                {(status === 'processing' || status === 'confirming') && (
                    <div className="rounded-3xl p-10 max-w-md w-full text-center bg-slate-900/90 border border-white/20 shadow-2xl backdrop-blur-xl">
                        <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 bg-[#b8a080]/15 border border-[#b8a080]/30 animate-pulse">
                            <i className="ri-loader-4-line text-4xl text-[#b8a080] animate-spin"></i>
                        </div>
                        <h1 className="text-2xl font-bold text-white mb-2">
                            {status === 'confirming' ? 'Ticketing with LetSFG...' : 'Connecting to Paystack...'}
                        </h1>
                        <p className="text-slate-300 text-sm leading-relaxed">
                            {status === 'confirming'
                                ? 'Payment confirmed! We are now requesting live booking confirmation from the airline & LetSFG.'
                                : 'Please complete your secure card/transfer payment in the Paystack popup.'}
                        </p>
                    </div>
                )}

                {/* ─── Idle: Ready to Pay ─── */}
                {status === 'idle' && (
                    <div className="rounded-3xl p-8 max-w-md w-full text-center bg-slate-900/90 border border-white/20 shadow-2xl backdrop-blur-xl">
                        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5 bg-[#b8a080]/15 border border-[#b8a080]/30">
                            <i className="ri-secure-payment-line text-3xl text-[#b8a080]"></i>
                        </div>
                        <h1 className="text-2xl font-bold text-white mb-1">Confirm Payment</h1>
                        <p className="text-slate-400 text-sm mb-6">Live Paystack Checkout</p>

                        <div className="rounded-2xl p-4 mb-6 bg-white/5 border border-white/10 text-left space-y-2.5">
                            {cartItems?.map((item: any, i: number) => (
                                <div key={i} className="flex justify-between items-center text-sm">
                                    <span className="text-slate-300 capitalize flex items-center gap-2">
                                        <i className={item.type === 'flight' ? 'ri-flight-takeoff-line text-[#b8a080]' : 'ri-hotel-line text-[#b8a080]'}></i>
                                        {item.type} ({item.details?.origin ? `${item.details.origin} → ${item.details.destination}` : (item.details?.name || 'Reservation')})
                                    </span>
                                    <span className="text-white font-medium">{formatPrice(item.price)}</span>
                                </div>
                            ))}
                            <div className="flex justify-between items-center pt-3 mt-2 border-t border-white/10">
                                <span className="text-white font-bold text-base">Total Amount:</span>
                                <span className="text-[#b8a080] font-extrabold text-xl">{formatPrice(totalAmount)}</span>
                            </div>
                        </div>

                        <p className="text-slate-400 text-xs mb-6">
                            <i className="ri-user-line mr-1 text-[#b8a080]"></i>
                            {passengers?.length} passenger{passengers?.length > 1 ? 's' : ''} · {contactEmail}
                        </p>

                        <div className="space-y-3">
                            <button
                                className="w-full py-4 rounded-2xl font-bold text-white text-base tracking-wide transition-all shadow-xl shadow-green-600/25 hover:shadow-green-600/40 cursor-pointer flex items-center justify-center gap-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 transform hover:-translate-y-0.5 active:translate-y-0"
                                onClick={() => {
                                    setStatus('processing');
                                    initializePayment({ onSuccess, onClose });
                                }}
                            >
                                <i className="ri-shield-check-fill text-xl"></i>
                                <span>Pay {formatPrice(totalAmount)} with Paystack</span>
                            </button>
                            <button
                                className="w-full py-3 rounded-xl font-medium text-slate-400 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-colors cursor-pointer"
                                onClick={() => navigate(-1)}
                            >
                                Back to Checkout
                            </button>
                        </div>

                        <p className="mt-5 text-[11px] text-slate-400 flex items-center justify-center gap-1.5">
                            <i className="ri-lock-line text-green-400"></i>
                            <span>256-bit SSL encryption · Powered by Live Paystack & LetSFG</span>
                        </p>
                    </div>
                )}
            </div>
            <Footer />
        </>
    );
}
